import React from "react";

import { Link } from "@remix-run/react";

import { getFullDisplayName } from "~/lib/display-name";
import { RadioCard } from "~/components/ui/radio-card";
import { logger } from "~/lib/logger";
import type { Practitioner } from "~/hooks/queries/use-user-details";
import { useUserDetails } from "~/hooks/queries/use-user-details";
import { isArchivedPractitionerClientStatus } from "~/lib/practitioner-client-access";
import { useUserProfile } from "~/lib/user";

import { Alert, AlertDescription } from "../ui/alert";
import { H3, P } from "../ui/typography";
interface PractitionerSelectorProps {
    selectedPractitionerId: string | null;
    onChange: (
        practitionerId: string,
        practitionerEmail?: string | null,
    ) => void;
    label?: string;
}

export const PractitionerSelector: React.FC<PractitionerSelectorProps> = ({
    selectedPractitionerId,
    onChange,
    label = "Who was this session with?",
}) => {
    const { data, isLoading, error } = useUserDetails();
    const { data: userProfile } = useUserProfile();

    if (isLoading) return <></>;
    if (error) return <></>;

    // AIDEV-NOTE: Only active practitioners are selectable. Archived links stay
    // listed but greyed out so the client understands why they can't assign to
    // that practitioner (docs/specs/client-archive.md); the server rejects it too.
    const allPractitioners = data?.practitioners ?? [];
    const activePractitioners = allPractitioners.filter(
        (p) => p.status === "active",
    );
    const archivedPractitioners = allPractitioners.filter((p) =>
        isArchivedPractitionerClientStatus(p.status),
    );

    // AIDEV-NOTE: Show selector if user has ANY practitioners (including inactive) or is a practitioner.
    // Previously only checked active practitioners, which hid the selector (and "Other" option)
    // when all practitioners were inactive — leaving users stuck unable to proceed.
    if (!allPractitioners.length && !data?.isPractitioner) {
        return null;
    }

    // AIDEV-NOTE: Show invite message if user has no active practitioners
    const showPractitionerInviteMessage =
        data?.isPractitioner && activePractitioners.length === 0;

    return (
        <div>
            <H3 className="font-medium text-brand-forest-800">{label}</H3>
            {showPractitionerInviteMessage && (
                <Alert className="mb-3">
                    <AlertDescription>
                        <P className="text-sm">
                            Want to share this session with your own
                            practitioner?{" "}
                            <Link
                                to="/client/invite-practitioner"
                                className="font-medium text-brand-terra-500 underline hover:text-brand-terra-600"
                            >
                                Invite them here
                            </Link>
                            .
                        </P>
                    </AlertDescription>
                </Alert>
            )}
            <div className="grid grid-cols-1 gap-3">
                {activePractitioners.map((practitioner: Practitioner) => (
                    <RadioCard
                        key={practitioner.id}
                        labelClassName="text-sm font-normal"
                        label={getFullDisplayName(
                            practitioner,
                            "Unknown Practitioner",
                        )}
                        value={practitioner.id}
                        name="practitioner"
                        checked={selectedPractitionerId === practitioner.id}
                        onChange={() => {
                            logger
                                .withMetadata({
                                    practitionerId: practitioner.id,
                                })
                                .info("Upload: practitioner selected");
                            onChange(practitioner.id, practitioner.email);
                        }}
                    />
                ))}
                {archivedPractitioners.map((practitioner: Practitioner) => (
                    <RadioCard
                        key={practitioner.id}
                        labelClassName="text-sm font-normal"
                        label={getFullDisplayName(
                            practitioner,
                            "Unknown Practitioner",
                        )}
                        description="Not accepting new sessions"
                        value={practitioner.id}
                        name="practitioner"
                        checked={false}
                        disabled
                        aria-disabled
                        onChange={() => {}}
                    />
                ))}
                {data?.isPractitioner && (
                    <RadioCard
                        labelClassName="text-sm font-normal"
                        label="I'm the practitioner"
                        value="self"
                        name="practitioner"
                        checked={
                            selectedPractitionerId === "self" ||
                            selectedPractitionerId === userProfile?.id
                        }
                        onChange={() => {
                            logger
                                .withMetadata({
                                    practitionerId: userProfile?.id || "self",
                                })
                                .info("Upload: practitioner selected");
                            onChange(
                                userProfile?.id || "self",
                                userProfile?.email,
                            );
                        }}
                    />
                )}
                <RadioCard
                    labelClassName="text-sm font-normal"
                    label="Other"
                    value="other"
                    name="practitioner"
                    checked={selectedPractitionerId === "other"}
                    onChange={() => {
                        logger
                            .withMetadata({ practitionerId: "other" })
                            .info("Upload: practitioner selected");
                        onChange("other", null);
                    }}
                />
            </div>
        </div>
    );
};
